/* eslint-disable */
import * as Sentry from '@sentry/browser';

import { EasyClaimAppState } from '../easyclaim.api.client.typescript/src/index';
import { reportWarning } from '../easyclaim.app.libs/reportWarning';

/**
 * Wraps the experimental `Performance.measureUserAgentSpecificMemory()` API and reports
 * the result to Sentry as an Application Metrics gauge for memory-usage diagnostics.
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/measureUserAgentSpecificMemory
 *
 * The API is only exposed in Chromium-based browsers and only while the page is
 * running in a cross-origin isolated context, i.e. served with the response headers:
 *   Cross-Origin-Opener-Policy: same-origin
 *   Cross-Origin-Embedder-Policy: require-corp
 * Enabling COEP is a project-wide decision (it blocks cross-origin resources that
 * don't send a matching CORP/CORS header), so it is intentionally not toggled here.
 * Until it is enabled, `isSupported()` returns `false` and no measurement is taken.
 */

export interface MemoryAttribution {
	url: string;
	scope: string;
}

export interface MemoryBreakdownEntry {
	bytes: number;
	attribution: MemoryAttribution[];
	types: string[];
}

export interface MemoryMeasurementResult {
	bytes: number;
	breakdown: MemoryBreakdownEntry[];
}

declare global {
	interface Performance {
		measureUserAgentSpecificMemory?: () => Promise<MemoryMeasurementResult>;
	}
}

const BYTES_PER_MEGABYTE = 1024 * 1024;

function createSessionId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export class MemoryMeasurementService {
	private readonly sessionId = createSessionId();
	private hasLoggedSupportStatus = false;
	private lastReportedMegabytes: number | undefined;

	/**
	 * True when the browser exposes the API and the page is cross-origin isolated,
	 * which are both required for the measurement to succeed.
	 */
	public isSupported(): boolean {
		const isSupported =
			typeof performance !== 'undefined' &&
			typeof performance.measureUserAgentSpecificMemory === 'function' &&
			typeof window !== 'undefined' &&
			window.crossOriginIsolated === true;

		if (!this.hasLoggedSupportStatus) this.hasLoggedSupportStatus = true;

		return isSupported;
	}

	/**
	 * Measures the memory currently used by this browsing context, including same-origin
	 * iframes and workers it owns. Resolves to `null` when unsupported, or when the browser
	 * rejects the call (e.g. it enforces a minimum interval between successive measurements).
	 */
	public async measure(): Promise<MemoryMeasurementResult | null> {
		if (!this.isSupported()) {
			return null;
		}

		try {
			const result = await performance.measureUserAgentSpecificMemory!();

			return result;
		} catch (error) {
			reportWarning(`[MemoryMeasurementService] measureUserAgentSpecificMemory() failed`, {
				sessionId: this.sessionId,
				timestamp: new Date().toISOString(),
				error,
			});

			return null;
		}
	}

	/**
	 * Measures the current memory usage and, if the measurement succeeded and the total
	 * differs from the last reported value, records it as a Sentry Application Metrics gauge
	 * (`memory_usage`, visible in Sentry's Metrics explorer, trace-connected) instead of
	 * raising a Sentry issue, along with the per-origin breakdown, session id, and current
	 * app screen as attributes, so memory usage (e.g. after heavy image processing) can be
	 * tracked in production. Measurements that report the same megabyte total as the
	 * previous report are skipped to avoid flooding Sentry with unchanged readings.
	 */
	public async measureAndReport(
		screen?: EasyClaimAppState
	): Promise<MemoryMeasurementResult | null> {
		const result = await this.measure();

		if (!result) {
			return null;
		}

		const totalMegabytes = Math.round(result.bytes / BYTES_PER_MEGABYTE);

		if (totalMegabytes === this.lastReportedMegabytes) return result;

		this.lastReportedMegabytes = totalMegabytes;

		const shortLink =
			typeof window !== 'undefined' ? window.location.pathname + window.location.search : 'unknown';

		const host = typeof window !== 'undefined' ? window.location.host : 'unknown';

		const breakdown = result.breakdown.map((entry) => ({
			megabytes: Math.round(entry.bytes / BYTES_PER_MEGABYTE),
			types: entry.types,
			attribution: entry.attribution,
		}));

		const memory = (performance as any).memory;
		const used = Math.round(memory.usedJSHeapSize / (1024 * 1024));
		const total = Math.round(memory.totalJSHeapSize / (1024 * 1024));
		const efficiency = (used / total) * 100;

		const additionalData = {
			// System info
			deviceMemory: `${(navigator as any).deviceMemory} GB`,
			platform: navigator.platform,
			userAgent: navigator.userAgent,

			// Accessibility
			isTabActive: document.hasFocus(),
			visibilityState: document.visibilityState,

			// Network
			networkConnection: (navigator as any).connection
				? {
						effectiveType: (navigator as any).connection.effectiveType,
						downlink: (navigator as any).connection.downlink,
					}
				: 'unknown',

			// Screen dimensions
			windowWidth: window.innerWidth,
			windowHeight: window.innerHeight,
		};

		const attributes = {
			memoryMeasurementSessionId: this.sessionId,
			shortLink: shortLink,
			host: host,
			screen: screen ?? 'unknown',
			JSHeapTotal: `${total} MB`,
			JSHeapUsed: `${used} MB`,
			efficiency: `${Math.round(efficiency)}%`,
			cpuCores: `${navigator.hardwareConcurrency}`,
			...additionalData,
			breakdown: JSON.stringify(breakdown),
		};

		Sentry.metrics.gauge('memory_usage', totalMegabytes, {
			unit: 'megabyte',
			attributes: attributes,
		});

		return result;
	}
}
